import { useEffect, useState, useRef, useCallback } from "react";
import { MessageDisplayType, ErrorStackDataType } from "@preveil-api";
import { FSMessage, FSRole, FSStatus } from "src/common/keys/protos/collections_pb";
import {
  Account, AppConfiguration, useAppSelector, useGetGrants, GrantSet, LinkEntity, Helpers, DriveEntryType, mapEntryType, DriveErrorMessages,
  useAppDispatch, MessageAnchors, MessageHandlerDisplayType, Message, Directory, useSendRequestMutation, useGetPermissions, CollectionFilesyncAPI, PermissionSet, PermissionSetType,
  buildNavigationLink, PathInfo, getEnumKey, DriveItemType, DriveRequest, ErrorStackItem, useFindLinkIdMutation, FindLinkApiResponse, isDisjointedACLNode, useBrowseLinkMutation,
  DriveRoot, DirectoryEntity, useGetCollectionInfo, EntryItemBase, DriveRouteType, MaintainerGrantsMap, GrantSetType
} from "src/common";
import { uiActions, driveActions, DriveCallbackAsyncFunction } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

// Description: This hook renders the parent directory for a node with no true root but one or more effective roots 
// This case happens when the user is the recipient of a shared acl node without a true root parent 
// resulting in the creation of an artificial directory to house the directory(s) with acl
export function useGetACLNodeDirectory(current_account: Account, set_state: boolean = true) {
  const dispatch = useAppDispatch();
  const default_permissions = useAppSelector((state: RootState) => state.drive.default_permissions);
  const default_grants = useAppSelector((state: RootState) => state.drive.default_grants);
  const [path_info, setPathInfo] = useState<PathInfo[]>([]);
  const [entry_base, setEntryBase] = useState<EntryItemBase | undefined>();
  const [error, setError] = useState<boolean>(false);
  const [directory_entity, setDirectoryEntity] = useState<DirectoryEntity>();
  const { permissions, getPermissions, destroyPermissions } = useGetPermissions(current_account);
  const { grants, getGrants, getAclKeyHistory, destroyGrants, error: grants_error } = useGetGrants(current_account);
  const { collection_info_error } = useGetCollectionInfo(current_account, entry_base, true);
  const [browseLink] = useBrowseLinkMutation();
  const [findLink] = useFindLinkIdMutation();
  const [sendRequest] = useSendRequestMutation();

  // Description: Store the currently working link entity
  const link_entity_ref = useRef<LinkEntity>();
  const node_queue_ref = useRef<FSMessage.Dir[]>([]);
  const working_node_ref = useRef<FSMessage.Dir>();

  // Description: Allow active permissions to be accessed
  const permissions_ref = useRef(permissions);
  function setPermissionsRef(_permissions?: PermissionSetType) {
    permissions_ref.current = _permissions;
  }
  function getPermissionsRef() {
    return permissions_ref.current;
  }

  // Description: Maintainer nodes fetched grants
  const maintainer_grants_ref = useRef<MaintainerGrantsMap>(new Map<string, GrantSetType>);
  function setMaintainerGrantsRef(id: string, maintainer_grants: GrantSetType) {
    maintainer_grants_ref.current.set(id, maintainer_grants);
  }
  function getMaintainerGrantsRef(id: string): GrantSetType | undefined {
    return maintainer_grants_ref.current.get(id);
  }

  // Description: Allow active path Info to be accessed
  const active_path_info_ref = useRef(path_info);
  function setActivePathInfoRef(_path_info: PathInfo[]) {
    active_path_info_ref.current = _path_info;
    setPathInfo(_path_info);
  }

  // Description: Get Permissions
  useEffect(() => {
    if (!!permissions && !!link_entity_ref.current) {
      setPermissionsRef(permissions);
      fetchCollectionAttributes(link_entity_ref.current, permissions);
      destroyPermissions();
    }
  }, [permissions]);

  // Description: Add the missing reader grants to the active grants object 
  useEffect(() => {
    if (!!grants) {
      if (!!link_entity_ref.current) {
        setMaintainerGrantsRef(grants.id, grants);
        getNext();
      } else {
        handleItemError(grants.id);
      }
      destroyGrants();
    }

    // LABEL GRANTS AS ERRORED - 
    grants_error && handleItemError(link_entity_ref.current?.id || "N/A");
  }, [grants, grants_error]);

  // Description: useEffect hook for when grants (from the useGetGrants hook) is initialized.
  useEffect(() => {
    !!collection_info_error && handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_collection_information }, null, MessageHandlerDisplayType.toastr);
  }, [collection_info_error]);

  // Description: Get Collection Attributes for the Collection Name
  async function fetchCollectionAttributes(_link: LinkEntity, _permissions: PermissionSetType) {
    const drive_request = await CollectionFilesyncAPI.getCollectionAttributes(current_account, _permissions, _link.collection_id);
    async function callback(message: FSMessage) {
      if (message.getStatus() === FSStatus.OK) {
        const wrapped_name = message.getCollectionAttributes()?.getName()?.getContent_asU8();
        const key_version = message.getCollectionAttributes()?.getName()?.getKeyVersion();
        if (!!_permissions && !!wrapped_name) {
          try {
            const permission_set = PermissionSet.init(_permissions);
            const permission = permission_set.permission(FSRole.READER, key_version) || permission_set.permission(FSRole.ACCESS, key_version);
            const user_key = !!permission ? await permission.key(current_account) : null;
            const decoded = !!user_key ? Helpers.utf8Decode(await user_key.encryption_key.unseal(wrapped_name)) : null;
            !!decoded ?
              setActivePathInfoRef([{
                path: decoded,
                id: _link.id,
                url: buildNavigationLink(_link.collection_id, _link.id, DriveEntryType.DIR),
                type: DriveEntryType.DIR
              }]) :
              handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_decrypting_name });
            // NOTE: Initialize the fetch of the entries after grabbing the collection attributes
            AppConfiguration.buildForWeb() ? getNext() : //  initFetchGrants(_link, _permissions) :
              (_link.id === DriveRouteType.DIRECT_LINK ? handleFindLinkId(_link) : (handleFetchLinkApp(_link)));
          } catch (stack_error: unknown) {
            handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_decrypting_acl_node_entry, stack_error });
          }
        } else {
          handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_collection_protocol_version });
        }
      } else {
        handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_collection_information });
      }

    }
    handleSendRequest(drive_request, callback, DriveErrorMessages.error_fetching_collection_information);
  }

  // -----------------------------
  // Web build path
  // -----------------------------
  function getNext() {
    working_node_ref.current = node_queue_ref.current.shift();
    const collection_id = link_entity_ref.current?.collection_id;
    const _permissions = getPermissionsRef();
    if (!!link_entity_ref.current && !!collection_id && !!_permissions) {
      if (!!working_node_ref.current) {
        const dir = working_node_ref.current;
        const maintainer_id = dir.getMaintainerId_asB64();
        const existing_grants = getMaintainerGrantsRef(maintainer_id);
        // NOTE: Skip getting grants if already fetched and cached
        if (!!existing_grants) {
          getNext();
        } else {
          const acl_list = dir.getAclList();
          const current_grants = !!collection_id ? GrantSet.getCurrentUsersGrantSets(collection_id, maintainer_id, acl_list, current_account.user_id) : null;
          if (!!current_grants) {
            setMaintainerGrantsRef(maintainer_id, current_grants);
            const grant = GrantSet.getGrantByRole(current_grants);
            // NOTE: if we have all the grants move to next
            !!grant ? getNext() :
              getAclKeyHistory(maintainer_id, collection_id, default_grants, _permissions, GrantSet.init(current_grants));
          } else {
            getGrants(maintainer_id, collection_id, default_permissions, default_grants);
          }
        }
      } else {
        buildDirectory(link_entity_ref.current);
      }
    } else {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_undefined_variables });
    }
  }

  // Description: Build ACL Node Directory from ACL_TREE > getDirsList() > FSMessage.Dir
  async function buildDirectory(_link: LinkEntity) {
    const collection_id = _link.collection_id;
    const list = _link.acl_tree?.getDirsList();
    try {
      // NOTE: We need to filter out all directories that are acl nodes and do not belong here as they are not direct children of the effective root
      const pseudo_entries = await Promise.all(_.map(list, async (dir: FSMessage.Dir) => {
        // NOTE: Only list disjointed acl nodes. Not children of children dir that are part of the acltree
        if (isDisjointedACLNode(dir)) {
          const maintainer_id = dir.getMaintainerId_asB64();
          const current_grants = getMaintainerGrantsRef(maintainer_id);
          const decoded_name = !!current_grants ? await Directory.getDirectoryMaintainersName(dir, current_account, current_grants) :
            DriveItemType.Shared_Folder;
          if (!!decoded_name) {
            const entry_info = mapEntryType(DriveEntryType.DIR, decoded_name);
            return {
              id: dir.getId_asB64(),
              collection_id,
              name: decoded_name,
              type: DriveEntryType.DIR,
              size: 0,
              deleted: false,
              deleted_at: 0,
              lastModificationDate: "",
              type_label: entry_info?.type_label,
              mapped_type: entry_info?.mapped_type,
              type_class: entry_info?.type_class,
              linked_collection_id: "",
              parent_id: dir.getParentId_asB64(), // NOTE: this will be "" as it is filtered by this prop (isDisjointedACLNode)
              starred: dir.getIsFavorite()
            };
          } else {
            const stack_message = DriveErrorMessages.error_decrypting_acl_node_entry.replace(MessageAnchors.node_id, dir.getId_asB64());
            handlePageErrorMessage(DriveErrorMessages.default, { stack_message });
          }
        }
      }));

      // NOTE: removes any undefined values from the array
      const entries = _.compact(pseudo_entries);
      const directory = Directory.buildDirFromACLTree(entries, _link);
      handleSuccess(directory.item, _link);
    } catch (stack_error) {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_message: "Failed to build directory from links acl tree", stack_error });
    }
  }

  // -----------------------------
  // APP - Filesync
  // -----------------------------
  // Description: This is a direct link and call handleFindLinkId before handleFetchLinkApp
  async function handleFindLinkId(_link: LinkEntity) {
    findLink({
      user_id: current_account.user_id,
      collection_id: _link.collection_id
    }).unwrap()
      .then((response: FindLinkApiResponse) => {
        handleFetchLinkApp(Object.assign({}, _link, { id: response.id }));
      })
      .catch((stack_error: unknown) => {
        handlePageErrorMessage(DriveErrorMessages.error_finding_direct_link,
          {
            stack_message: DriveErrorMessages.error_fetching_link,
            stack_error
          }, null, MessageHandlerDisplayType.toastr);
      });
  }

  // Description: APP mode - fetch the ACL node directory contents from Filesync (browseLink).
  // Web builds the directory locally via getNext() since there is no Filesync daemon in the browser.
  async function handleFetchLinkApp(_link: LinkEntity) {
    const link_id = _link.id;
    if (!!_link.linked_collection_id && !!link_id) {
      browseLink({
        user_id: current_account.user_id,
        collection_id: _link.linked_collection_id,
        link_id
      })
        .unwrap()
        .then((response: DriveRoot) => {
          const directory_entity = Directory.parseFSLink(response, { collection_id: _link.collection_id, id: link_id });
          handleSuccess(directory_entity, _link);
        })
        .catch((stack_error: unknown) => {
          handlePageErrorMessage(DriveErrorMessages.default,
            {
              stack_message: DriveErrorMessages.error_fetching_link,
              stack_error
            }, null, MessageHandlerDisplayType.toastr);
        });
    } else {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_message: DriveErrorMessages.error_fetching_link }, null, MessageHandlerDisplayType.toastr);
    }
  }

  // -----------------------------
  // General functions
  // -----------------------------
  // Description: Handle success for current_directory and current_collection
  function handleSuccess(directory_entity: DirectoryEntity, _link: LinkEntity) {
    setDirectoryEntity(directory_entity);
    if (set_state) {
      dispatch(driveActions.browseDirectorySuccess(directory_entity));
      dispatch(driveActions.setLinkEntity(undefined));
      // NOTE: get Node Permissions for lists - Get Collection Info for listview
      setEntryBase({
        linked_collection_id: _link.collection_id,
        collection_id: directory_entity.collection_id,
        id: directory_entity.id,
        type: DriveEntryType.LINK
      });
    }
  }

  // Description: Reset hook values
  function reset(): void {
    link_entity_ref.current = undefined;
    node_queue_ref.current = [];
    working_node_ref.current = undefined;
    setError(false);
    destroyPermissions();
    destroyGrants();
  }


  // Description: Sends Request and handles error when the request object is null, pass it custom stack message
  function handleSendRequest<T>(request: DriveRequest | null, callback: DriveCallbackAsyncFunction<T>, stack_error: string = DriveErrorMessages.error_sending_request) {
    if (!!request) {
      request.setCallback(callback);
      sendRequest(request);
    } else {
      handlePageErrorMessage(DriveErrorMessages.default, { stack_error, stack_message: DriveErrorMessages.error_sending_request }, null);
    }
  }

  // Description: Queue error and move to next item
  function handleItemError(id: string) {
    dispatch(uiActions.handleRequestErrors(
      new Message(DriveErrorMessages.error_handle_queue_item,
        MessageHandlerDisplayType.logger),
      { stack_message: `item failed: ${id}` }));
    getNext();
  }

  // Description: Handle error message and send error to store
  function handlePageErrorMessage(message: string, stack_data?: ErrorStackDataType, fsmessage?: FSMessage | null, display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
    const status = !!fsmessage?.getStatus() ? getEnumKey(FSStatus, Number(fsmessage.getStatus())) : "empty";
    const stack = new ErrorStackItem("[useGetACLNodeDirectory Hook]", fsmessage?.getErrorMessage() || message, status || "error", stack_data).info;
    dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
    setError(true);
  }

  // Description: Callback to build the effective root directory entries
  //              Onload sort through the acl_tree directory and build the entries from the fsmessage.dir[]
  const fetchAclDirectory = useCallback((_link_entity: LinkEntity) => {
    node_queue_ref.current = _link_entity.acl_tree?.getDirsList().slice() || [];
    link_entity_ref.current = _link_entity;
    getPermissions(_link_entity.collection_id, default_permissions);
  }, []);

  // Description: reset hook 
  const resetAclDirectory = useCallback(() => {
    reset();
  }, []);

  return {
    fetchAclDirectory,
    resetAclDirectory,
    directory_entity,
    error
  };
}
